/**
 * Match score math (llm-workflows.md §4.5) — the DOMAIN owns ALL scoring; the RAW
 * match pass only grades per-dimension strengths + names anti-value hits from evidence.
 * The weights + bands + penalty live HERE, in pure deterministic code, so a Match is
 * auditable and the model never sees the arithmetic.
 *
 * Re-derived (NOT ported) from the legacy `opportunity/score.ts`: the career `Match` is
 * alignment-first (`MatchSignal.dimension` ∈ fit/social/motivational/goal); scores are
 * `UnitInterval` [0,1]. **The 4-value `MatchTier` (strong | viable | weak | unsuited)
 * REPLACES the legacy 3-value `strong | moderate | weak` @≥0.7/≥0.4 — new bands below.**
 * The anti-value penalty is SEMANTIC (from evidence-backed findings the model reports),
 * never a lexical keyword filter (the appgen anti-value lesson).
 *
 * WHY IT LIVES HERE (moved from `api-v2/src/search/score.ts`, W4): the header above has
 * always said the DOMAIN owns the scoring, and the file's only import was a type-only one
 * from this package — it had no api-v2 dependency at all. It sat in `api-v2` anyway, which
 * put it out of reach of `app-elements`. The W3 »Wonach ich entscheide« weighting control
 * has to show the user what their weights do to a real score AS THEY MOVE THEM, and the one
 * unacceptable way to do that is a second copy of the arithmetic in the client. So the math
 * moved to the package both sides already depend on; `api-v2/src/search/score.ts` is now a
 * re-export, and there is still exactly ONE implementation.
 *
 * Runtime-neutral: pure functions over domain types, no imports beyond this package's own.
 */

import type { AlignmentDimension, MatchSignal, MatchTier, ProfilerRubric } from "./inferred"

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n)

/**
 * Importance-weighted alignment score in [0,1]. When a W3 `ProfilerRubric` is present,
 * each dimension's weight is the sum of its criteria weights; otherwise every graded
 * dimension weighs equally. Σ(weight × strength) / Σ(weight); 0 when nothing graded.
 */
export function computeMatchScore(signals: MatchSignal[], rubric?: ProfilerRubric | null): number {
  const weightByDim = new Map<AlignmentDimension, number>()
  if (rubric) {
    for (const c of rubric.criteria) {
      weightByDim.set(c.dimension, (weightByDim.get(c.dimension) ?? 0) + clamp01(c.weight))
    }
  }
  let weighted = 0
  let totalWeight = 0
  for (const s of signals) {
    const w = rubric ? (weightByDim.get(s.dimension) ?? 0) : 1
    if (w === 0) continue
    weighted += clamp01(s.strength) * w
    totalWeight += w
  }
  if (totalWeight === 0) return 0
  return clamp01(weighted / totalWeight)
}

/** Penalty a genuine anti-value hit subtracts from the [0,1] score (legacy 0.45). */
export const ANTI_VALUE_PENALTY = 0.45

/**
 * Apply the SEMANTIC anti-value penalty: when the RAW pass reported ≥1 evidence-backed
 * anti-value hit, the score drops by `ANTI_VALUE_PENALTY` ONCE (not stacked; clamped to
 * 0). No lexical keyword matching — the model decides hits from evidence, the domain
 * only applies the arithmetic.
 */
export function penaltyFromHits(score: number, antiValueHits: string[]): number {
  return antiValueHits.length > 0 ? clamp01(score - ANTI_VALUE_PENALTY) : score
}

/**
 * Map a [0,1] score to the 4-value `MatchTier`. Re-derived bands for the new vocabulary
 * (the legacy 3-value `strong≥0.7 / moderate≥0.4 / weak` is DEAD): a bottom `unsuited`
 * band splits off the clearly-off matches so `weak` means "real but thin", not "reject".
 */
export function tierForScore(score: number): MatchTier {
  if (score >= 0.7) return "strong"
  if (score >= 0.45) return "viable"
  if (score >= 0.2) return "weak"
  return "unsuited"
}

/** Ordinal rank (unsuited < weak < viable < strong) — for the W2 `minTier` floor. */
const TIER_RANK: Record<MatchTier, number> = { unsuited: 0, weak: 1, viable: 2, strong: 3 }

/** Does `tier` meet the W2 quality floor? `null`/undefined floor ⇒ no floor (always true). */
export function tierAtLeast(tier: MatchTier, floor: MatchTier | null | undefined): boolean {
  if (!floor) return true
  return TIER_RANK[tier] >= TIER_RANK[floor]
}
